
import React, { useEffect, useState } from 'react';
import { collection, addDoc, updateDoc, doc, onSnapshot, query, orderBy, deleteDoc, where, getDocs } from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import LeadForm from './components/LeadForm';
import LeadCard from './components/LeadCard';
import Tabs from './components/Tabs';
import Login from './components/Login';
import UncalledToggle from './components/UncalledToggle';
import WeeklyAgenda from './components/WeeklyAgenda';
import DailyTodos from './components/DailyTodos';
import DailyNotes from './components/DailyNotes';

function normalizePhone(p=''){return String(p).replace(/[^\d+]/g,'').replace(/^1(\d{10})$/,'$1');}
function isSameLocalDay(iso){if(!iso)return false;const d=new Date(iso);if(isNaN(d))return false;const n=new Date();return d.getFullYear()===n.getFullYear()&&d.getMonth()===n.getMonth()&&d.getDate()===n.getDate();}
function sameDay(iso,ymd){if(!iso||!ymd)return false;const d=new Date(iso);const y=d.getFullYear();const m=String(d.getMonth()+1).padStart(2,'0');const da=String(d.getDate()).padStart(2,'0');return `${y}-${m}-${da}`===ymd;}

function AppInner(){
  const categories=[
    {id:'buyers',label:'Buyers'},
    {id:'sellers',label:'Sellers'},
    {id:'steel',label:'Steel Cable Sellers'},
    {id:'inventory',label:'Current Belt Inventory'},
    {id:'agenda',label:'Weekly Sales Agenda'},
    {id:'todos',label:'Daily To-Dos'},
  ];

  const [leads,setLeads]=useState([]);
  const [active,setActive]=useState('buyers');
  const [search,setSearch]=useState('');
  const [dateFilter,setDateFilter]=useState('');
  const [onlyUncalled,setOnlyUncalled]=useState(false);
  const [editing,setEditing]=useState(null);

  useEffect(()=>{
    const qy=query(collection(db,'leads'),orderBy('createdAt','desc'));
    const unsub=onSnapshot(qy,snap=>setLeads(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return ()=>unsub();
  },[]);

  const onSave=async(lead)=>{
    const normalized={...lead,phone:normalizePhone(lead.phone||'')};
    const cat=normalized.category||active;
    if(normalized.phone){
      const dupQ=query(collection(db,'leads'),where('phone','==',normalized.phone),where('category','==',cat));
      const dupSnap=await getDocs(dupQ);
      const duplicateDoc=dupSnap.docs.find(d=>d.id!==(normalized.id||''));
      if(duplicateDoc){const e=new Error('DUPLICATE_PHONE');e.userMessage='A lead with this phone already exists in this tab.';throw e;}
    }
    if(normalized.dateCalled && typeof normalized.dateCalled==='string' && normalized.dateCalled.length===10){
      normalized.dateCalled=new Date(normalized.dateCalled+'T12:00:00').toISOString();
    }
    try{
      if(normalized.id){
        await updateDoc(doc(db,'leads',normalized.id),{...normalized,category:cat,updatedAt:new Date().toISOString()});
      }else{
        await addDoc(collection(db,'leads'),{...normalized,category:cat,notes:normalized.notes||[],createdAt:new Date().toISOString(),updatedAt:new Date().toISOString()});
      }
      setEditing(null);
    }catch(e){if(e.userMessage)throw e;console.error(e);alert('Error saving lead.');}
  };

  const onDelete=async(id)=>{if(!window.confirm('Delete this lead?'))return;try{await deleteDoc(doc(db,'leads',id));}catch(e){console.error(e);alert('Error deleting lead.');}};
  const startEdit=(lead)=>{setEditing(lead);window.scrollTo({top:0,behavior:'smooth'});};

  const filtered=leads.filter(l=>{
    if(!['agenda','todos','notes'].includes(active)&&l.category!==active) return false;
    if(['agenda','todos','notes'].includes(active)) return false;
    const q=search.trim().toLowerCase();
    const matchesText=!q||(l.name||'').toLowerCase().includes(q)||(l.company||'').toLowerCase().includes(q)||(l.position||'').toLowerCase().includes(q);
    const matchesDate=!dateFilter||sameDay(l.dateCalled,dateFilter);
    const matchesUncalled=!onlyUncalled||!l.dateCalled||String(l.dateCalled).trim()==='';
    return matchesText&&matchesDate&&matchesUncalled;
  });
  const calledTodayCount=(['agenda','todos','notes'].includes(active))?0:leads.filter(l=>l.category===active&&isSameLocalDay(l.dateCalled)).length;

  return(<div className='app'>
    <header className='topbar'>
      <div className='brand'>EZA Recycling</div>
      <Tabs categories={categories} active={active} onChange={(id)=>{setActive(id);setEditing(null);}}/>
      <div className='stats'>
        {!['agenda','todos','notes'].includes(active)&&(<div className='today-pill'>Called Today: <strong>{calledTodayCount}</strong></div>)}
        <span style={{marginRight:12,opacity:0.9}}>{auth.currentUser?.email}</span>
        <button className='btn small' onClick={()=>signOut(auth)}>Sign Out</button>
      </div>
    </header>

    <main className='container'>
      {active==='agenda'?(
        <div className='right' style={{width:'100%'}}><WeeklyAgenda/></div>
      ):active==='todos'?(
        <div className='right' style={{width:'100%'}}><DailyTodos/></div>
      ):active==='notes'?(
        <div className='right' style={{width:'100%'}}><DailyNotes/></div>
      ):(
        <>
          <div className='left'>
            <LeadForm key={editing?editing.id:'new'} onSave={onSave} editing={editing} onCancel={()=>setEditing(null)} currentCategory={active}/>
          </div>
          <div className='right'>
            <div className='controls'>
              <input className='search' placeholder='Search by name, company, or position' value={search} onChange={e=>setSearch(e.target.value)}/>
              <div className='date-filter'>
                <label>Filter by Date Called</label>
                <input type='date' value={dateFilter} onChange={e=>setDateFilter(e.target.value)}/>
                {dateFilter&&<button className='btn small' onClick={()=>setDateFilter('')}>Clear</button>}
              </div>
              <UncalledToggle value={onlyUncalled} onChange={setOnlyUncalled}/>
              <div className='counts'>{filtered.length} lead(s)</div>
            </div>
            <div className='grid'>
              {filtered.map(lead=>(
                <LeadCard key={lead.id} lead={lead} onEdit={()=>startEdit(lead)} onDelete={()=>onDelete(lead.id)}/>
              ))}
              {filtered.length===0&&<div className='empty'>No leads match your filters.</div>}
            </div>
          </div>
        </>
      )}
    </main>
  </div>);
}

export default function App(){
  const [user,setUser]=useState(null); const [ready,setReady]=useState(false);
  useEffect(()=>{return onAuthStateChanged(auth,(u)=>{setUser(u);setReady(true);});},[]);
  if(!ready) return <div style={{padding:24}}>Loading…</div>;
  if(!user) return <Login/>;
  if(!user.email||!/.*@ezarecycling\.com$/i.test(user.email)){
    return(<div style={{padding:24}} className='card'>
      <h3>Access Restricted</h3><p>This app is for @ezarecycling.com accounts only.</p>
      <button className='btn small' onClick={()=>signOut(auth)}>Sign Out</button></div>);
  }
  return <AppInner/>;
}
